import { useState } from "react";
import axios from "axios";
import { Lock, Unlock, TrendingUp, AlertTriangle } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  AreaChart, Area, CartesianGrid, ReferenceLine,
} from "recharts";
import { FIN, CASHFLOW, sensitivityProfit, SENS_PRICES, SENS_LAND } from "../../content";
import { FadeIn, ChapterLabel } from "./Chrome";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#d4af37";
const CREAM = "#f2f0e9";
const STONE = "#8c877d";

export const InvestorGate = ({ t, lang, onUnlock }) => {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("checking");
    try {
      await axios.post(`${API}/investor/unlock`, { email, code, language: lang });
      onUnlock();
    } catch {
      setStatus("wrong");
    }
  };

  return (
    <section id="investors" className="min-h-screen flex items-center justify-center py-28 px-6 md:px-12" data-testid="section-investor-gate">
      <div className="max-w-md w-full">
        <ChapterLabel no={t.gate.no} label={t.gate.label} />
        <FadeIn delay={0.1}>
          <div className="flex items-center gap-4">
            <Lock size={22} className="text-[#d4af37]" />
            <h2 className="font-serif-display font-light text-3xl md:text-4xl lg:text-5xl text-[#f2f0e9]">{t.gate.title}</h2>
          </div>
        </FadeIn>
        <FadeIn delay={0.2}>
          <p className="mt-6 text-base text-[#a1a3a1] leading-relaxed">{t.gate.body}</p>
        </FadeIn>
        <FadeIn delay={0.3}>
          <form onSubmit={submit} className="mt-10 space-y-4" data-testid="investor-gate-form">
            <input required type="email" className="input-dark" placeholder={t.gate.email} value={email}
              onChange={(e) => setEmail(e.target.value)} data-testid="gate-email-input" />
            <input required className="input-dark tracking-[0.3em] uppercase" placeholder={t.gate.code} value={code}
              onChange={(e) => setCode(e.target.value)} data-testid="gate-code-input" />
            <button type="submit" disabled={status === "checking"} className="btn-gold w-full" data-testid="gate-submit-btn">
              {status === "checking" ? t.gate.checking : t.gate.submit}
            </button>
            {status === "wrong" && <p className="text-sm text-red-400" data-testid="gate-error-msg">{t.gate.wrong}</p>}
            <p className="text-xs text-[#8c877d] pt-2">{t.gate.hint}</p>
          </form>
        </FadeIn>
      </div>
    </section>
  );
};

const buildWaterfall = (t) => {
  const data = [{ name: "GDV", base: 0, val: FIN.gdv, kind: "total" }];
  let running = FIN.gdv;
  FIN.costs.forEach((c) => {
    running = Math.round((running - c.value) * 100) / 100;
    data.push({ name: c[t] || c.it, base: running, val: c.value, kind: "cost" });
  });
  data.push({ name: t === "it" ? "Utile lordo" : t === "ar" ? "إجمالي الربح" : "Gross profit", base: 0, val: running, kind: "profit" });
  return data;
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const p = payload.find((x) => x.dataKey === "val" || x.dataKey === "v");
  return (
    <div className="bg-[#121514] border border-white/15 px-4 py-2.5 text-sm">
      <div className="text-[#8c877d] text-xs">{label}</div>
      <div className="text-[#f2f0e9]">€{p ? p.value : ""}M</div>
    </div>
  );
};

export const InvestorDashboard = ({ t, lang }) => {
  const waterfall = buildWaterfall(lang);
  const inv = t.invest;

  return (
    <section id="investment-case" className="py-28 px-6 md:px-12 bg-[#0e100f] border-t border-[#d4af37]/30" data-testid="section-investor-dashboard">
      <div className="max-w-7xl mx-auto w-full">
        <FadeIn className="flex items-center gap-4 mb-8">
          <Unlock size={18} className="text-[#d4af37]" />
          <span className="text-xs md:text-sm tracking-[0.3em] uppercase text-[#d4af37]">{inv.label}</span>
        </FadeIn>
        <FadeIn delay={0.1}>
          <h2 className="font-serif-display font-light text-4xl md:text-5xl lg:text-6xl leading-tight text-[#f2f0e9]">{inv.title}</h2>
        </FadeIn>
        <FadeIn delay={0.2}>
          <p className="mt-6 text-sm text-[#8c877d] italic max-w-3xl border-l-2 border-[#d4af37]/50 pl-4">{inv.disclaimer}</p>
        </FadeIn>

        {/* KPI grid */}
        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-white/10 border border-white/10" data-testid="investor-kpi-grid">
          {inv.kpis.map((k, i) => (
            <FadeIn key={i} delay={0.06 * i} className="bg-[#0e100f] p-6 min-h-[130px] flex flex-col justify-between">
              <div className="font-serif-display text-2xl md:text-3xl text-[#d4af37]">{k.v}</div>
              <div className="text-xs text-[#8c877d] mt-3 leading-snug">{k.l}</div>
            </FadeIn>
          ))}
        </div>

        {/* Waterfall */}
        <FadeIn delay={0.1} className="mt-16 border border-white/10 p-6 md:p-10" >
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp size={16} className="text-[#d4af37]" />
            <h3 className="font-serif-display text-2xl md:text-3xl text-[#f2f0e9]">{inv.waterfallTitle}</h3>
          </div>
          <p className="text-xs text-[#8c877d] mb-8">{inv.waterfallNote}</p>
          <div className="h-[380px]" data-testid="waterfall-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={waterfall} margin={{ top: 10, right: 10, left: -10, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(242,240,233,0.06)" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: STONE, fontSize: 10 }} angle={-28} textAnchor="end" interval={0} axisLine={{ stroke: "rgba(242,240,233,0.1)" }} tickLine={false} />
                <YAxis tick={{ fill: STONE, fontSize: 11 }} axisLine={false} tickLine={false} unit="M" />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(212,175,55,0.05)" }} />
                <Bar dataKey="base" stackId="wf" fill="transparent" isAnimationActive={false} />
                <Bar dataKey="val" stackId="wf" radius={[2, 2, 0, 0]}>
                  {waterfall.map((d, i) => (
                    <Cell key={i} fill={d.kind === "total" ? CREAM : d.kind === "profit" ? GOLD : STONE} fillOpacity={d.kind === "cost" ? 0.55 : 1} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </FadeIn>

        {/* Cashflow */}
        <FadeIn delay={0.1} className="mt-10 border border-white/10 p-6 md:p-10">
          <h3 className="font-serif-display text-2xl md:text-3xl text-[#f2f0e9] mb-2">{inv.cashflowTitle}</h3>
          <p className="text-xs text-[#8c877d] mb-8 max-w-3xl">{inv.cashflowNote}</p>
          <div className="h-[320px]" data-testid="cashflow-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={CASHFLOW} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="cfGold" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={GOLD} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(242,240,233,0.06)" vertical={false} />
                <XAxis dataKey="m" tick={{ fill: STONE, fontSize: 11 }} axisLine={{ stroke: "rgba(242,240,233,0.1)" }} tickLine={false}
                  label={{ value: lang === "it" ? "mesi" : lang === "ar" ? "شهر" : "months", position: "insideBottomRight", fill: STONE, fontSize: 10, offset: -2 }} />
                <YAxis tick={{ fill: STONE, fontSize: 11 }} axisLine={false} tickLine={false} unit="M" />
                <Tooltip content={<ChartTooltip />} />
                <ReferenceLine y={0} stroke="rgba(242,240,233,0.25)" />
                <Area type="monotone" dataKey="v" stroke={GOLD} strokeWidth={2} fill="url(#cfGold)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </FadeIn>

        {/* Sensitivity */}
        <FadeIn delay={0.1} className="mt-10 border border-white/10 p-6 md:p-10 overflow-x-auto">
          <h3 className="font-serif-display text-2xl md:text-3xl text-[#f2f0e9] mb-2">{inv.sensTitle}</h3>
          <p className="text-xs text-[#8c877d] mb-8 max-w-3xl">{inv.sensNote}</p>
          <table className="w-full min-w-[560px] text-sm" data-testid="sensitivity-table">
            <thead>
              <tr>
                <th className="text-left p-3 text-xs text-[#8c877d] font-normal border-b border-white/10">
                  {inv.sensLand} ↓ / {inv.sensPrice} →
                </th>
                {SENS_PRICES.map((p) => (
                  <th key={p} className={`p-3 font-normal border-b border-white/10 ${p === 5 ? "text-[#d4af37]" : "text-[#a1a3a1]"}`}>
                    €{p.toFixed(1)}M
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {SENS_LAND.map((l) => (
                <tr key={l}>
                  <td className={`p-3 border-b border-white/5 ${l === 5 ? "text-[#d4af37]" : "text-[#a1a3a1]"}`}>€{l}M</td>
                  {SENS_PRICES.map((p) => {
                    const base = p === 5 && l === 5;
                    return (
                      <td key={p} data-testid={`sens-cell-${p}-${l}`}
                        className={`p-3 text-center border-b border-white/5 font-serif-display text-lg ${
                          base ? "bg-[#d4af37]/15 text-[#d4af37] border border-[#d4af37]/50" : "text-[#f2f0e9]/85"
                        }`}>
                        {sensitivityProfit(p, l).toFixed(1)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </FadeIn>

        {/* Risk slide */}
        <FadeIn delay={0.1} className="mt-10 border border-amber-500/30 bg-amber-500/[0.04] p-6 md:p-10" >
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle size={18} className="text-amber-400" />
            <h3 className="font-serif-display text-2xl md:text-3xl text-[#f2f0e9]" data-testid="risk-title">{inv.riskTitle}</h3>
          </div>
          <p className="text-base text-[#a1a3a1] leading-relaxed max-w-3xl">{inv.riskBody}</p>
          <div className="mt-8 grid gap-px bg-white/10 border border-white/10">
            {inv.riskScenarios.map((s, i) => (
              <div key={i} className="bg-[#0e100f] p-7">
                <div className="font-serif-display text-xl text-[#d4af37]">{s.t}</div>
                <p className="mt-3 text-sm text-[#a1a3a1] leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </FadeIn>

        {/* Notes */}
        <div className="mt-10 grid md:grid-cols-2 gap-6">
          <FadeIn className="text-xs text-[#8c877d] leading-relaxed border-l border-white/15 pl-4">{inv.taxNote}</FadeIn>
          <FadeIn delay={0.1} className="text-xs text-[#8c877d] leading-relaxed border-l border-white/15 pl-4">{inv.alignNote}</FadeIn>
        </div>

        <FadeIn delay={0.15} className="mt-14 border border-[#d4af37]/40 p-8 text-center">
          <p className="font-serif-display italic text-lg md:text-xl text-[#f2f0e9]/85" data-testid="investor-cta-note">{inv.cta}</p>
        </FadeIn>
      </div>
    </section>
  );
};
